import {
  Button,
  Drawer,
  Flex,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  TableColumnsType,
  TableProps,
  Typography
} from "antd";
import { TPrivilege, TRole } from "api/services/models/types";
import { saveRole } from "api/services/UserService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { getAxiosErrorMessage } from "helpers/utils";
import { useCallback, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";

type TAddOrUpdateRoleProps = {
  onClose: (role?: TRole) => void;
  role?: TRole;
  allPrivileges: TPrivilege[];
};

const columns: TableColumnsType<TPrivilege> = [
  {
    key: "id",
    title: "Privilege",
    dataIndex: "description"
  }
];

const AddOrUpdateRole = (props: TAddOrUpdateRoleProps) => {
  const { accountId } = useTDAuthentication();
  const [role, setRole] = useState<Partial<TRole>>(
    props.role
      ? { ...props.role }
      : {
          description: ""
        }
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    role.privileges?.map(p => p.id) ?? []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>();

  const onRoleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^a-zA-Z0-9 _\-]/g, "");
    setRole(r => ({ ...role, description: inputValue }));
  };

  const rowSelection: TableProps<TPrivilege>["rowSelection"] = {
    type: "checkbox",
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => setSelectedRowKeys(selectedRowKeys)
  };

  const onSave = useCallback(() => {
    if (role.description == null || role.description.length < 3) {
      setError("Role Name should be atleast 3 characters");
      return;
    }
    if (accountId) {
      role.privileges = props.allPrivileges.filter(p => selectedRowKeys.some(k => k === p.id));
      setError(null);
      setLoading(true);
      saveRole(accountId, role)
        .then(r => props?.onClose(r.data))
        .catch(e => setError(getAxiosErrorMessage(e)))
        .finally(() => setLoading(false));
    }
  }, [accountId, role, selectedRowKeys]);

  return (
    <Modal
      title={
        <Flex
          wrap
          gap="middle"
          align="start"
          justify="space-between"
          style={{ marginBottom: "16px" }}>
          <span>{props.role ? "Edit Role" : "Add Role"}</span>
          <Space>
            <Button onClick={onSave} 
            type="primary">Save</Button>
            <Button
              style={{ border: "none" }}
              icon={<CloseOutlined style={{ fontSize: ".75rem" }} />}
              onClick={e => props.onClose()}></Button>
          </Space>
        </Flex>
      }
      open={true}
      maskClosable={false}
      footer={null}
      closeIcon={null}
      destroyOnClose={true}>
      {loading && <Flex justify="space-around" style={{ width: "100%", textAlign: "center", padding:"1rem" }}>
            <Spin tip="Saving, please wait..." />
          </Flex>}
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      <Space direction="vertical" size={"small"}>
      <label htmlFor="roleName" className="input-label">
            Role Name
            </label>
        <Input
          id="roleName"
          placeholder="Role Name"
          value={role.description}
          maxLength={128}
          onChange={onRoleNameChange}
          className="filter-input-transparent"
        />
        <Table<TPrivilege>
          rowSelection={rowSelection}
          columns={columns}
          rowKey={"id"}
          dataSource={props.allPrivileges}
          size="small"
          pagination={false}
          id="privilege-grid"
          scroll={{ y: 400 }}
        />
      </Space>
    </Modal>
  );
};

export default AddOrUpdateRole;
